import React, { useEffect, useState } from 'react';

type OverlayRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type DimmedOverlayProps = {
  active: boolean;
  targetId: string;
  opacity: number;
  zIndex?: number;
  disableHeader?: boolean;
};

const DimmedOverlay: React.FC<DimmedOverlayProps> = ({
  active,
  targetId,
  opacity,
  zIndex = 1500,
  disableHeader = false,
}) => {
  const [rect, setRect] = useState<OverlayRect | null>(null);

  useEffect(() => {
    const header = disableHeader ? document.getElementById('header') : null;

    if (!active) {
      if (header) header.style.pointerEvents = '';
      setRect(null);
      return;
    }

    const updateRect = () => {
      const target = document.getElementById(targetId);
      if (!target) return;
      const bounds = target.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = Math.max(0, bounds.left);
      const top = Math.max(0, bounds.top);
      const right = Math.min(viewportWidth, bounds.right);
      const bottom = Math.min(viewportHeight, bounds.bottom);

      setRect({
        top,
        left,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
      });
    };

    updateRect();
    if (header) header.style.pointerEvents = 'none';
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      if (header) header.style.pointerEvents = '';
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [active, disableHeader, targetId]);

  if (!active || !rect) return null;

  const color = `rgba(0, 0, 0, ${opacity})`;

  return (
    <>
      <div
        className="fixed left-0 top-0 right-0"
        style={{ height: rect.top, backgroundColor: color, zIndex }}
      />
      <div
        className="fixed left-0"
        style={{
          top: rect.top,
          height: rect.height,
          width: rect.left,
          backgroundColor: color,
          zIndex,
        }}
      />
      <div
        className="fixed right-0"
        style={{
          top: rect.top,
          height: rect.height,
          left: rect.left + rect.width,
          backgroundColor: color,
          zIndex,
        }}
      />
      <div
        className="fixed left-0 right-0 bottom-0"
        style={{ top: rect.top + rect.height, backgroundColor: color, zIndex }}
      />
    </>
  );
};

export default DimmedOverlay;
